import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Lock, Mail, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await login(email, password);
      navigate('/dashboard/overview');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid email or password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="amigos-app amg-login-wrap" data-testid="dashboard-login">
      <div className="film-grain" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="amg-login-card"
      >
        <div style={{ textAlign: 'center', marginBottom: '2.4rem' }}>
          <div className="amg-login-shield">
            <Shield size={34} />
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--text-primary)', letterSpacing: '0.12em', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
            Secure Access
          </h1>
          <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', fontFamily: 'var(--font-alt)', letterSpacing: '0.08em' }}>
            Amigo&apos;s Bail Bonds | License #0111
          </p>
        </div>

        <form onSubmit={submit}>
          <div style={{ marginBottom: '1.25rem' }}>
            <label className="form-label" htmlFor="login-email">
              <Mail size={14} style={{ display: 'inline-block', marginRight: 6, verticalAlign: 'text-bottom' }} /> Email
            </label>
            <input
              id="login-email"
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              data-testid="login-email-input"
              style={{ fontSize: '1rem' }}
            />
          </div>
          <div style={{ marginBottom: '1.6rem' }}>
            <label className="form-label" htmlFor="login-password">
              <Lock size={14} style={{ display: 'inline-block', marginRight: 6, verticalAlign: 'text-bottom' }} /> Password
            </label>
            <input
              id="login-password"
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              required
              data-testid="login-password-input"
              style={{ fontSize: '1rem' }}
            />
          </div>

          {error && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: 'var(--red-bright)', fontSize: '0.86rem', marginBottom: '1rem', padding: '0.6rem 0.8rem', background: 'rgba(122, 30, 40, 0.08)', border: '1px solid rgba(122, 30, 40, 0.3)' }} data-testid="login-error">
              <AlertCircle size={15} /> {error}
            </div>
          )}

          <button
            type="submit"
            className="amg-btn-primary"
            disabled={busy}
            style={{ width: '100%' }}
            data-testid="login-submit-btn"
          >
            {busy ? <><Loader2 size={16} className="spinner" /> Signing In\u2026</> : 'Login'}
          </button>
        </form>

        <div className="titan-authority" data-testid="titan-aegis-badge">
          <div className="titan-authority__head">
            <div className="titan-authority__seal">
              <Shield size={22} />
            </div>
            <div>
              <div className="titan-authority__title">TITN Aegis Security Module</div>
              <div className="titan-authority__sub">Custom Developed by Texas IT Nerds</div>
            </div>
          </div>
          <p className="titan-authority__intro">
            Every byte of client and staff data on this platform is protected by a defense-in-depth stack engineered by <strong style={{ color: 'var(--text-primary)' }}>James Hudson @ Texas IT Nerds</strong>.
          </p>
          <ul className="titan-authority__list">
            <li><Shield size={12} /><span><strong>AES-256</strong> at-rest encryption</span></li>
            <li><Shield size={12} /><span><strong>TLS 1.3</strong> in-transit encryption</span></li>
            <li><Shield size={12} /><span><strong>bcrypt</strong> password hashing (12 rounds)</span></li>
            <li><Shield size={12} /><span><strong>JWT</strong> signed session tokens</span></li>
            <li><Shield size={12} /><span><strong>Rate-limited</strong> auth (8/min brute-force lock)</span></li>
            <li><Shield size={12} /><span><strong>RBAC</strong> role-scoped permissions</span></li>
            <li><Shield size={12} /><span><strong>GridFS</strong> encrypted PDF vault</span></li>
            <li><Shield size={12} /><span><strong>Immutable</strong> audit-log trail</span></li>
            <li><Shield size={12} /><span><strong>CORS</strong> origin allow-listing</span></li>
            <li><Shield size={12} /><span><strong>MongoDB Atlas</strong> encrypted storage</span></li>
          </ul>
          <div className="titan-authority__footer">
            Protected by <strong>TITN Aegis Security</strong> · Texas IT Nerds
          </div>
        </div>

        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <Link to="/" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textDecoration: 'none', fontFamily: 'var(--font-alt)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            <ArrowLeft size={12} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} /> Back to Site
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
